import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { signOut } from '../store/slices/authSlice';
import {
  clearCountryDetails,
  clearPageDetails,
  fetchWebAnalyticsBrowsers,
  fetchWebAnalyticsCountries,
  fetchWebAnalyticsCountryDetails,
  fetchWebAnalyticsPageDetails,
  fetchWebAnalyticsSummary,
} from '../store/slices/webAnalyticsSlice';
import { colors } from '../constants/colors';
import Sidebar from '../components/(admin)/Sidebar';
import PageTopBar from '../components/(admin)/PageTopBar';
import PageDetailsModal from '../components/(admin)/PageDetailsModal';
import CountriesDetailsModal from '../components/(admin)/CountriesDetailsModal';
import ExportReportsButton from '../components/(admin)/ExportReportsButton';
import DeviceBreakdownChart from '../components/(admin)/charts/DeviceBreakdownChart';
import BrowserBreakdownChart from '../components/(admin)/charts/BrowserBreakdownChart';
import TrafficTrendChart from '../components/(admin)/charts/TrafficTrendChart';
import EventBreakdownList from '../components/(admin)/charts/EventBreakdownList';
import {
  formatChange,
  mergeReferrers,
  mergeTopPages,
} from '../utils/webAnalyticsDisplay';
import { countryFlagEmoji, mergeTopCountries } from '../utils/webAnalyticsGeo';
import {
  WA_SURFACE,
  WEB_ANALYTICS_COLORS,
} from '../constants/webAnalyticsTheme';

const formatNumber = (value) => (Number(value) || 0).toLocaleString();

const SkeletonBlock = ({ height = 16, width = '100%', style = {} }) => (
  <div className="wa-skeleton" style={{ height, width, borderRadius: 8, ...style }} />
);

const TinyBar = ({ pct, color }) => (
  <div
    style={{
      height: 4,
      borderRadius: 999,
      backgroundColor: '#F3F4F6',
      overflow: 'hidden',
      marginTop: 8,
    }}
  >
    <div
      style={{
        width: `${Math.max(4, Math.min(100, pct))}%`,
        height: '100%',
        borderRadius: 999,
        backgroundColor: color,
      }}
    />
  </div>
);

const WebAnalytics = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [selectedTimeRange, setSelectedTimeRange] = useState('30');

  const {
    summary,
    topPages,
    topReferrers,
    byDevice,
    topCountries,
    byBrowser,
    eventBreakdown,
    trafficOverTime,
    loading,
    browsersLoading,
    error,
    pageDetails,
    pageDetailsLoading,
    pageDetailsError,
    countryDetails,
    countryDetailsLoading,
    countryDetailsError,
  } = useSelector((state) => state.webAnalytics);

  useEffect(() => {
    const days = parseInt(selectedTimeRange, 10);
    dispatch(fetchWebAnalyticsSummary(days));
    dispatch(fetchWebAnalyticsCountries(days));
    dispatch(fetchWebAnalyticsBrowsers(days));
  }, [dispatch, selectedTimeRange]);

  const handleOpenPageDetails = (page) => {
    const path = page?.path;
    if (!path) return;
    dispatch(
      fetchWebAnalyticsPageDetails({
        path,
        days: parseInt(selectedTimeRange, 10),
      })
    );
  };

  const handleClosePageDetails = () => dispatch(clearPageDetails());

  const handleOpenCountryDetails = (country) => {
    const code = country?.countryCode;
    if (!code) return;
    dispatch(
      fetchWebAnalyticsCountryDetails({
        countryCode: code,
        days: parseInt(selectedTimeRange, 10),
      })
    );
  };

  const handleCloseCountryDetails = () => dispatch(clearCountryDetails());

  const handleSignOut = async () => {
    setIsSigningOut(true);
    try {
      await dispatch(signOut()).unwrap();
    } catch (err) {
      console.error('Logout failed:', err);
    } finally {
      setTimeout(() => {
        navigate('/signin');
        setIsSigningOut(false);
      }, 500);
    }
  };

  const pages = useMemo(() => mergeTopPages(topPages), [topPages]);
  const referrers = useMemo(() => mergeReferrers(topReferrers), [topReferrers]);
  const countries = useMemo(
    () => mergeTopCountries(topCountries),
    [topCountries]
  );
  const maxPageViews = Math.max(...pages.map((p) => p.views || 0), 1);
  const maxReferrerCount = Math.max(...referrers.map((r) => r.count || 0), 1);

  const hasAnyData =
    (summary?.events || 0) > 0 || (summary?.page_views || 0) > 0;
  const showDashboardSkeleton = loading && !hasAnyData;

  const countryDisplayName = (country) => {
    const name = country?.country || 'Unknown';
    if (name === 'Unknown' || country?.countryCode === 'XX') {
      return 'Unknown Location';
    }
    return name;
  };

  const cardStyle = {
    backgroundColor: colors.background.primary,
    borderRadius: WA_SURFACE.radius,
    border: `1px solid ${WA_SURFACE.border}`,
    boxShadow: WA_SURFACE.shadow,
    padding: WA_SURFACE.cardPadding,
  };

  const equalCardStyle = {
    ...cardStyle,
    minHeight: 320,
    display: 'flex',
    flexDirection: 'column',
  };

  const chartTitleStyle = {
    fontSize: 15,
    fontWeight: 650,
    color: colors.text.primary,
    margin: 0,
    letterSpacing: '-0.01em',
  };

  const chartSubtitleStyle = {
    margin: '4px 0 0',
    fontSize: 12,
    color: colors.text.secondary,
  };

  const grid2Style = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 24,
    marginBottom: WA_SURFACE.sectionGap,
    alignItems: 'stretch',
  };

  const filterSelectStyle = {
    padding: '8px 12px',
    fontSize: 14,
    border: `1px solid ${WA_SURFACE.border}`,
    borderRadius: 10,
    backgroundColor: colors.background.primary,
    color: colors.text.primary,
    cursor: 'pointer',
  };

  const emptyState = (title, subtitle) => (
    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        color: colors.text.secondary,
        padding: 16,
        minHeight: 160,
      }}
    >
      <p style={{ margin: 0, fontWeight: 600, fontSize: 14 }}>{title}</p>
      <p style={{ margin: '6px 0 0', fontSize: 12 }}>{subtitle}</p>
    </div>
  );

  const kpis = [
    {
      key: 'page_views',
      label: 'Page Views',
      value: summary?.page_views,
      change: summary?.changes?.page_views,
      accent: WEB_ANALYTICS_COLORS.page_views,
    },
    {
      key: 'unique_visitors',
      label: 'Visitors',
      value: summary?.unique_visitors,
      change: summary?.changes?.unique_visitors,
      accent: WEB_ANALYTICS_COLORS.unique_visitors,
    },
    {
      key: 'sessions',
      label: 'Sessions',
      value: summary?.sessions,
      change: summary?.changes?.sessions,
      accent: WEB_ANALYTICS_COLORS.sessions,
    },
    {
      key: 'events',
      label: 'Events',
      value: summary?.events,
      change: summary?.changes?.events,
      accent: WEB_ANALYTICS_COLORS.events,
    },
  ];

  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: colors.background.secondary,
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        display: 'flex',
      }}
    >
      <style>
        {`
          .wa-scroll {
            scrollbar-width: thin;
            scrollbar-color: ${colors.borderVariants.medium} transparent;
          }
          .wa-scroll::-webkit-scrollbar { width: 6px; }
          .wa-scroll::-webkit-scrollbar-thumb {
            background: ${colors.borderVariants.medium};
            border-radius: 999px;
          }
          .wa-skeleton {
            background: linear-gradient(
              90deg,
              ${colors.background.tertiary} 25%,
              ${colors.background.secondary} 50%,
              ${colors.background.tertiary} 75%
            );
            background-size: 200% 100%;
            animation: wa-skeleton-pulse 1.4s ease-in-out infinite;
          }
          @keyframes wa-skeleton-pulse {
            0% { background-position: 200% 0; }
            100% { background-position: -200% 0; }
          }
          @media (max-width: 960px) {
            .wa-grid-2 { grid-template-columns: 1fr !important; }
            .wa-kpi-grid { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
          }
        `}
      </style>

      <Sidebar />

      <div
        style={{
          flex: 1,
          marginLeft: 280,
          padding: 32,
          backgroundColor: colors.background.secondary,
        }}
      >
        <div style={{ maxWidth: 1600, margin: '0 auto', width: '100%' }}>
          <PageTopBar
            title="Web Analytics"
            onAiConfig={() => navigate('/ai-configuration')}
            onEmbeddingGuide={() => navigate('/embedding-guide')}
            onSignOut={handleSignOut}
          />

          <div
            style={{
              display: 'flex',
              gap: 16,
              marginBottom: WA_SURFACE.sectionGap,
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <select
              style={filterSelectStyle}
              value={selectedTimeRange}
              onChange={(e) => setSelectedTimeRange(e.target.value)}
            >
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
            </select>
            <div style={{ marginLeft: 'auto' }}>
              <ExportReportsButton
                days={parseInt(selectedTimeRange, 10)}
                disabled={!hasAnyData}
              />
            </div>
          </div>

          {error && (
            <div
              style={{
                ...cardStyle,
                marginBottom: 24,
                border: '1px solid #fecaca',
                backgroundColor: '#fef2f2',
                color: colors.status.error,
                fontSize: 14,
              }}
            >
              {error}
            </div>
          )}

          {!loading && !hasAnyData ? (
            <div style={{ ...cardStyle, padding: '56px 32px', textAlign: 'center' }}>
              <h3
                style={{
                  margin: 0,
                  fontSize: 20,
                  fontWeight: 700,
                  color: colors.text.primary,
                }}
              >
                No analytics available yet
              </h3>
              <p
                style={{
                  margin: '10px auto 0',
                  maxWidth: 460,
                  fontSize: 14,
                  lineHeight: 1.6,
                  color: colors.text.secondary,
                }}
              >
                Start tracking visitors to see data here. Install the chat widget
                on your website and metrics will appear automatically.
              </p>
              <button
                type="button"
                onClick={() => navigate('/embedding-guide')}
                style={{
                  marginTop: 20,
                  padding: '10px 16px',
                  borderRadius: 10,
                  border: 'none',
                  backgroundColor: colors.primary,
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: 14,
                  cursor: 'pointer',
                }}
              >
                View embedding guide
              </button>
            </div>
          ) : showDashboardSkeleton ? (
            <>
              <div
                className="wa-kpi-grid"
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
                  gap: 16,
                  marginBottom: WA_SURFACE.sectionGap,
                }}
              >
                {[0, 1, 2, 3].map((i) => (
                  <div key={i} style={cardStyle}>
                    <SkeletonBlock height={28} width="40%" style={{ marginBottom: 10 }} />
                    <SkeletonBlock height={12} width="50%" />
                  </div>
                ))}
              </div>
              <div style={{ ...cardStyle, marginBottom: WA_SURFACE.sectionGap }}>
                <SkeletonBlock height={240} />
              </div>
            </>
          ) : (
            <>
              {/* KPI Cards */}
              <div
                className="wa-kpi-grid"
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
                  gap: 16,
                  marginBottom: WA_SURFACE.sectionGap,
                }}
              >
                {kpis.map((kpi) => {
                  const change =
                    summary?.comparison_available && kpi.change != null
                      ? formatChange(kpi.change)
                      : null;
                  const changeColor =
                    change?.direction === 'up'
                      ? colors.status.success
                      : change?.direction === 'down'
                        ? colors.status.error
                        : colors.text.secondary;
                  return (
                    <div
                      key={kpi.key}
                      style={{
                        ...cardStyle,
                        position: 'relative',
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: 4,
                          height: '100%',
                          backgroundColor: kpi.accent,
                        }}
                      />
                      <div
                        style={{
                          fontSize: 32,
                          fontWeight: 700,
                          color: colors.text.primary,
                          letterSpacing: '-0.04em',
                          lineHeight: 1.1,
                        }}
                      >
                        {formatNumber(kpi.value)}
                      </div>
                      <div
                        style={{
                          marginTop: 8,
                          fontSize: 13,
                          fontWeight: 600,
                          color: colors.text.secondary,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                        }}
                      >
                        <span
                          style={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            backgroundColor: kpi.accent,
                            display: 'inline-block',
                          }}
                        />
                        {kpi.label}
                      </div>
                      {change ? (
                        <div
                          style={{
                            marginTop: 10,
                            fontSize: 12,
                            color: changeColor,
                            fontWeight: 600,
                          }}
                        >
                          {change.text} vs previous period
                        </div>
                      ) : (
                        <div
                          style={{
                            marginTop: 10,
                            fontSize: 12,
                            color: colors.text.light,
                          }}
                        >
                          Last {selectedTimeRange} days
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Traffic Over Time */}
              <div style={{ ...cardStyle, marginBottom: WA_SURFACE.sectionGap }}>
                <div style={{ marginBottom: 20 }}>
                  <h3 style={chartTitleStyle}>Traffic Over Time</h3>
                  <p style={chartSubtitleStyle}>
                    Daily page views for the last {selectedTimeRange} days
                  </p>
                </div>
                <TrafficTrendChart
                  data={trafficOverTime}
                  isLoading={loading && trafficOverTime.length === 0}
                  metric="page_views"
                  days={parseInt(selectedTimeRange, 10)}
                />
              </div>

              {/* Devices | Browsers */}
              <div className="wa-grid-2" style={grid2Style}>
                <div style={equalCardStyle}>
                  <div style={{ marginBottom: 16 }}>
                    <h3 style={chartTitleStyle}>Devices</h3>
                    <p style={chartSubtitleStyle}>
                      Desktop, mobile, tablet, and unknown
                    </p>
                  </div>
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                    <DeviceBreakdownChart
                      data={byDevice}
                      isLoading={loading && byDevice.length === 0}
                    />
                  </div>
                </div>

                <div style={equalCardStyle}>
                  <div style={{ marginBottom: 16 }}>
                    <h3 style={chartTitleStyle}>Browsers</h3>
                    <p style={chartSubtitleStyle}>Share of page views by browser</p>
                  </div>
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                    <BrowserBreakdownChart
                      data={byBrowser}
                      isLoading={browsersLoading && byBrowser.length === 0}
                    />
                  </div>
                </div>
              </div>

              {/* Countries | Referrers */}
              <div className="wa-grid-2" style={grid2Style}>
                <div style={equalCardStyle}>
                  <div style={{ marginBottom: 16 }}>
                    <h3 style={chartTitleStyle}>Top Countries</h3>
                    <p style={chartSubtitleStyle}>Visitor distribution by country</p>
                  </div>
                  {countries.length === 0 ? (
                    emptyState(
                      'No country data yet',
                      'Geo data is added when events are collected'
                    )
                  ) : (
                    <div
                      className="wa-scroll"
                      style={{
                        flex: 1,
                        overflowY: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        maxHeight: 360,
                      }}
                    >
                      {countries.map((country) => {
                        const visitors = country.visitors || 0;
                        const pct =
                          country.percentage != null ? country.percentage : null;
                        const widthPct = Math.max(
                          6,
                          pct != null
                            ? Math.round(pct)
                            : Math.round(
                                (visitors /
                                  Math.max(
                                    ...countries.map((c) => c.visitors || 0),
                                    1
                                  )) *
                                  100
                              )
                        );
                        return (
                          <button
                            key={country.countryCode}
                            type="button"
                            onClick={() => handleOpenCountryDetails(country)}
                            style={{
                              display: 'block',
                              width: '100%',
                              textAlign: 'left',
                              border: 'none',
                              background: 'transparent',
                              padding: '12px 8px',
                              cursor: 'pointer',
                              borderRadius: 10,
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#F9FAFB';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                gap: 12,
                              }}
                            >
                              <span
                                style={{
                                  fontSize: 14,
                                  fontWeight: 600,
                                  color: colors.text.primary,
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 10,
                                  minWidth: 0,
                                }}
                              >
                                <span style={{ fontSize: 18, lineHeight: 1 }}>
                                  {countryFlagEmoji(country.countryCode)}
                                </span>
                                <span
                                  style={{
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                  }}
                                >
                                  {countryDisplayName(country)}
                                </span>
                              </span>
                              <span
                                style={{
                                  fontSize: 13,
                                  fontWeight: 700,
                                  color: colors.text.primary,
                                  flexShrink: 0,
                                }}
                              >
                                {formatNumber(visitors)}
                                {pct != null ? (
                                  <span
                                    style={{
                                      marginLeft: 6,
                                      fontWeight: 600,
                                      color: colors.text.secondary,
                                    }}
                                  >
                                    · {pct}%
                                  </span>
                                ) : null}
                              </span>
                            </div>
                            <TinyBar
                              pct={widthPct}
                              color={WEB_ANALYTICS_COLORS.countries}
                            />
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div style={equalCardStyle}>
                  <div style={{ marginBottom: 16 }}>
                    <h3 style={chartTitleStyle}>Top Referrers</h3>
                    <p style={chartSubtitleStyle}>Where visitors came from</p>
                  </div>
                  {referrers.length === 0 ? (
                    emptyState(
                      'No referrers yet',
                      'Referral domains appear when visitors arrive from another site'
                    )
                  ) : (
                    <div
                      className="wa-scroll"
                      style={{
                        flex: 1,
                        overflowY: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 4,
                        maxHeight: 360,
                      }}
                    >
                      {referrers.map((ref, index) => {
                        const count = ref.count || 0;
                        const widthPct = Math.max(
                          6,
                          Math.round((count / maxReferrerCount) * 100)
                        );
                        return (
                          <div
                            key={`${ref.domain}-${index}`}
                            style={{ padding: '12px 8px' }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                gap: 12,
                              }}
                            >
                              <span
                                style={{
                                  fontSize: 13,
                                  fontWeight: 650,
                                  color: colors.text.primary,
                                  textTransform:
                                    ref.domain === 'direct' ? 'capitalize' : 'none',
                                }}
                              >
                                {ref.domain}
                              </span>
                              <span
                                style={{
                                  fontSize: 13,
                                  fontWeight: 700,
                                  color: colors.text.primary,
                                }}
                              >
                                {formatNumber(count)}
                              </span>
                            </div>
                            <TinyBar
                              pct={widthPct}
                              color={WEB_ANALYTICS_COLORS.referrers}
                            />
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Top Pages | Event Breakdown */}
              <div
                className="wa-grid-2"
                style={{ ...grid2Style, marginBottom: 8 }}
              >
                <div style={equalCardStyle}>
                  <div style={{ marginBottom: 16 }}>
                    <h3 style={chartTitleStyle}>Top Pages</h3>
                    <p style={chartSubtitleStyle}>Most viewed paths</p>
                  </div>
                  {pages.length === 0 ? (
                    emptyState(
                      'No page views yet',
                      'Embed the chat widget to start collecting traffic'
                    )
                  ) : (
                    <div
                      className="wa-scroll"
                      style={{
                        flex: 1,
                        overflowY: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 4,
                        maxHeight: 360,
                      }}
                    >
                      {pages.map((page, index) => {
                        const views = page.views || 0;
                        const widthPct = Math.max(
                          6,
                          Math.round((views / maxPageViews) * 100)
                        );
                        return (
                          <button
                            key={`${page.path}-${index}`}
                            type="button"
                            onClick={() => handleOpenPageDetails(page)}
                            title={page.url || page.full_path || page.path}
                            style={{
                              display: 'block',
                              width: '100%',
                              textAlign: 'left',
                              border: 'none',
                              background: 'transparent',
                              padding: '12px 8px',
                              cursor: 'pointer',
                              borderRadius: 10,
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#F9FAFB';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                gap: 12,
                                alignItems: 'flex-start',
                              }}
                            >
                              <div style={{ minWidth: 0, flex: 1 }}>
                                <div
                                  style={{
                                    fontSize: 13,
                                    fontWeight: 650,
                                    color: colors.text.primary,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                  }}
                                >
                                  {page.path || '/'}
                                </div>
                                {page.title ? (
                                  <div
                                    style={{
                                      fontSize: 12,
                                      color: colors.text.secondary,
                                      marginTop: 2,
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                    }}
                                  >
                                    {page.title}
                                  </div>
                                ) : null}
                              </div>
                              <div
                                style={{
                                  fontSize: 13,
                                  fontWeight: 700,
                                  color: colors.text.primary,
                                  flexShrink: 0,
                                }}
                              >
                                {formatNumber(views)}
                                <span
                                  style={{
                                    marginLeft: 4,
                                    fontWeight: 500,
                                    color: colors.text.light,
                                    fontSize: 11,
                                  }}
                                >
                                  views
                                </span>
                              </div>
                            </div>
                            <TinyBar
                              pct={widthPct}
                              color={WEB_ANALYTICS_COLORS.page_views}
                            />
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div style={equalCardStyle}>
                  <div style={{ marginBottom: 16 }}>
                    <h3 style={chartTitleStyle}>Event Breakdown</h3>
                    <p style={chartSubtitleStyle}>
                      Distribution of recorded analytics events.
                    </p>
                  </div>
                  <div
                    className="wa-scroll"
                    style={{ flex: 1, overflowY: 'auto', maxHeight: 360 }}
                  >
                    <EventBreakdownList
                      data={eventBreakdown}
                      isLoading={loading && eventBreakdown.length === 0}
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {isSigningOut && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.35)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
          }}
        >
          <div
            style={{
              backgroundColor: colors.background.primary,
              padding: '20px 28px',
              borderRadius: 12,
              fontWeight: 600,
              color: colors.text.primary,
            }}
          >
            Signing out…
          </div>
        </div>
      )}

      <CountriesDetailsModal
        open={
          !!countryDetails || countryDetailsLoading || !!countryDetailsError
        }
        onClose={handleCloseCountryDetails}
        details={countryDetails}
        loading={countryDetailsLoading}
        error={countryDetailsError}
        days={selectedTimeRange}
      />

      <PageDetailsModal
        open={!!pageDetails || pageDetailsLoading || !!pageDetailsError}
        onClose={handleClosePageDetails}
        details={pageDetails}
        loading={pageDetailsLoading}
        error={pageDetailsError}
        days={selectedTimeRange}
      />
    </div>
  );
};

export default WebAnalytics;
